import { useSnapshot } from 'valtio/react'
import {
  InfoKeyWithArrayValue,
  OnboardingInfo,
  OnboardingStore,
} from './OnboardingStore.ts'
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useAnimate,
  useInView,
} from 'framer-motion'
import { Button } from '../../shared/ui/button/button.tsx'
import { classed } from '@tw-classed/react'
import {
  createStoreContext,
  useIsMobile,
  useStoreContext,
  useTimeout,
} from '../../shared/lib/hooks.ts'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import {
  cn,
  getGenerationAnimDelay,
  range,
  typedIncludes,
  useMount,
} from '../../shared/lib/utils.ts'
import { TextInput } from '../../shared/ui/textInput/textInput.tsx'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import Rive from '@rive-app/react-canvas'
import { Navigate, useLocation } from 'react-router-dom'
import { urls } from '../../shared/urls.ts'
import { useAppModule } from '../appContext.ts'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  LanguageLevelKeys,
  OnboardingStep,
  onboardingSteps,
} from '../../shared/data/onboardingSteps.ts'
import { ClickableWords } from '../../shared/ui/ClickableWords/ClickableWords.tsx'
import { EdmanAvatar } from '../programs/programView.tsx'
import { ChooseListData } from './onboardingData.ts'
import { DotsAnimation } from '../../shared/ui/spinner.tsx'
import { ChartSvg } from '../../shared/ui/chart/chart.tsx'
import { AppMessageKeys } from '../../shared/translations/messages.ts'
import { TUTOR_NAME } from '../../shared/branding.ts'

export const OnboardingContext = createStoreContext<OnboardingStore>()

export function useOnboardingStore() {
  return useStoreContext(OnboardingContext)
}

const TimeSelect = () => {
  const store = useOnboardingStore()
  const items = useOnboardingStore().data.duration
  const state = useSnapshot(store.state)
  return (
    <div className="mt-24 flex h-32 items-center justify-between rounded-full bg-white p-2 text-14 mxs:mt-8 ">
      {items.map((time, index) => {
        const itemDuration = time[2]
        const preparedNumber = itemDuration.toString().split(' ')[0]
        const currentDuration = state.info.duration
        return (
          <React.Fragment key={itemDuration}>
            <button
              onClick={() => {
                store.setInfo('duration', preparedNumber)
              }}
              className={`h-26 w-full whitespace-nowrap rounded-full transition-all
              ${
                currentDuration === preparedNumber
                  ? 'bg-blue2 font-extrabold text-white'
                  : 'font-semibold text-coolgray650'
              }
            `}
            >
              {preparedNumber}{' '}
              {index === 0 || index === items.length - 1 ? (
                <FormattedMessage id="min" />
              ) : (
                ''
              )}
            </button>
            {index < items.length - 1 && (
              <div
                className={`mx-2 h-22 w-1 transition-all
                ${
                  currentDuration === items[index][2] ||
                  currentDuration === items[index + 1][2]
                    ? 'bg-white'
                    : 'bg-coolgray450'
                }
              `}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default TimeSelect

const DurationChartWithSelect = () => {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const items = store.data.duration
  const stateDuration = state.info.duration
  const durationIndex = items.findIndex(
    (el) => el[2].toString().split(' ')[0] === stateDuration,
  )
  return (
    <div className="mx-auto w-full">
      <div className="relative h-fit max-h-[40vh] w-full overflow-hidden rounded-[26px] bg-white pt-0">
        <AnimatePresence mode="wait">
          <motion.h2
            className="absolute inset-x-0 top-18 mx-auto mb-[-18px] text-center text-24 font-extrabold text-blue2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            key={`text-${stateDuration}`}
          >
            <FormattedMessage
              id="onboarding.minutesPerDay"
              values={{ minutes: stateDuration }}
            />
          </motion.h2>
        </AnimatePresence>
        <div className="size-full">
          <ChartSvg durationIndex={durationIndex} />
        </div>
      </div>
      <TimeSelect />
    </div>
  )
}

export const DurationSelectionStep = () => {
  const items = useOnboardingStore().data.duration
  const location = useLocation()
  const isUserProfile = location.pathname.includes('profile')

  return isUserProfile ? (
    <ChooseList
      className="flex flex-wrap gap-8"
      classNameButtons={cn('min-w-300 flex-1 shrink-0 sm:h-80')}
      infoKey="duration"
      items={items}
    />
  ) : (
    <DurationChartWithSelect />
  )
}

export const ImproveSelectionStep = () => {
  const items = useOnboardingStore().data.improves
  return (
    <ChooseList
      className="flex flex-wrap gap-8"
      classNameButtons={cn('min-w-300 flex-1 shrink-0')}
      infoKey="improves"
      items={items}
    />
  )
}

export const AgeSelectionStep = () => {
  const items = useOnboardingStore().data.age
  return (
    <ChooseList
      className="flex flex-wrap gap-8"
      classNameButtons={cn('min-w-300 flex-1 shrink-0 sm:h-56')}
      infoKey="age"
      items={items}
    />
  )
}

export const SexSelectionStep = () => {
  const items = useOnboardingStore().data.sex
  return (
    <ChooseList
      classNameButtons="flex-1"
      className="flex flex-col gap-8"
      infoKey="sex"
      items={items}
    />
  )
}

export const GoalSelectionStep = () => {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const location = useLocation()
  const isProfilePage = location.pathname.includes('/profile')

  const items = useOnboardingStore().data.goal
  const list = (invisible: boolean) => (
    <ChooseList
      className={cn('flex flex-wrap gap-8', invisible && 'invisible')}
      classNameButtons={cn(
        'shrink-0 sm:h-80',
        state.goalNumber && !invisible && !isProfilePage
          ? 'w-full sm:w-[calc(50%-4px)] '
          : 'min-w-300 flex-1',
      )}
      infoKey="goal"
      //   showOnlyIndex={
      //     state.goalNumber && !invisible && !isProfilePage
      //       ? state.goalNumber - 1
      //       : undefined
      //   }
      items={items}
      onChoose={(i) => {
        store.setGoalNumber((i + 1) as 1 | 2 | 3 | 4 | 5)
        store.updateSession()
      }}
    />
  )
  return (
    <div className="relative">
      {list(true)}
      <div className="absolute inset-0">{list(false)}</div>
    </div>
  )
}

export const ProfessionSelectionStep = () => {
  const items = useOnboardingStore().data.profession
  return (
    <ChooseList
      className="flex flex-wrap gap-8 sm:gap-12"
      infoKey="profession"
      items={items}
    />
  )
}

export const InterestSelectionStep = () => {
  const items = useOnboardingStore().data.interests
  return (
    <ChooseList
      className="flex flex-wrap gap-8 sm:gap-12"
      infoKey="interests"
      items={items}
    />
  )
}

export const LanguageLevelSelectionStep = () => {
  const store = useOnboardingStore()

  const items = useOnboardingStore().data.languageLevel
  const levels = useOnboardingStore().data.levels
  return (
    <ChooseList
      className="flex max-w-[800px] flex-col gap-4 text-start sm:gap-8"
      classNameButtons={cn('h-44 sm:h-auto')}
      infoKey="languageLevel"
      items={items}
      onChoose={(i) => {
        store.setLevel(levels[i], items[i][2] as LanguageLevelKeys)
      }}
    />
  )
}

function ChooseList(props: {
  infoKey: keyof OnboardingInfo
  className: string
  classNameButtons?: string
  showOnlyIndex?: number
  items: readonly ChooseListData[]
  onChoose?: (index: number) => void
}) {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const smallSize = new Set<keyof OnboardingInfo>([
    'improves',
    'profession',
    'interests',
    'languageLevel',
  ])
  const otherInputRef = useRef<HTMLInputElement>(null)
  const editOther = state.editOther
  const info = state.info
  const editable = ['none']
  const canBeEdit = editable.includes(props.infoKey)
  const isHalfWidthButton =
    props.infoKey === 'interests' || props.infoKey === 'goal'

  return (
    <div className={cn('', props.className)}>
      {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
      {props.items.map((item, i) => {
        const active = typedIncludes(InfoKeyWithArrayValue, props.infoKey)
          ? info[props.infoKey].includes(item[2].toString())
          : item[2] == info[props.infoKey]
        const last = i == props.items.length - 1
        return (
          <ChooseButton
            id={
              props.infoKey === 'languageLevel'
                ? `onboarding.languageLevel-${i}`
                : undefined
            }
            className={cn(
              props.showOnlyIndex !== undefined &&
                (i !== props.showOnlyIndex ? 'hidden' : ''),
              props.classNameButtons,
            )}
            size={smallSize.has(props.infoKey) ? 'small' : 'large'}
            width={isHalfWidthButton ? 'half' : 'full'}
            active={active}
            key={item[2]}
            onClick={() => {
              props.onChoose?.(i)
              if (last && canBeEdit) {
                store.setEditOther(true)
              } else {
                store.setInfo(props.infoKey, item[2].toString())
                const noAutoChangeStep: (keyof OnboardingInfo)[] = [
                  'interests',
                  'improves',
                  'goal',
                  'duration',
                ]
                if (!noAutoChangeStep.includes(props.infoKey)) {
                  store.changeStep(true)
                }
              }
            }}
          >
            <div className="flex items-center justify-center gap-8">
              {item[0] && <span className="text-20 sm:text-24">{item[0]}</span>}
              {editOther && last ? (
                <div className="flex gap-8">
                  <input
                    ref={otherInputRef}
                    className="border-b bg-transparent"
                    maxLength={30}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        store.updateOther(props.infoKey, e.currentTarget.value)
                      }
                    }}
                  />
                  <Button
                    size="small"
                    bg="main"
                    onClick={(e) => {
                      if (otherInputRef.current) {
                        e.stopPropagation()
                        store.updateOther(
                          props.infoKey,
                          otherInputRef.current.value,
                        )
                      }
                    }}
                  >
                    <FormattedMessage id="save" />
                  </Button>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-left">{item[1]}</div>
              )}
            </div>
          </ChooseButton>
        )
      })}
    </div>
  )
}

const EdmanTextBubble = classed.div(
  'size-fit min-w-76 rounded-r-[26px] rounded-bl-[26px] bg-white p-12 text-18 font-light leading-tight sm:rounded-[38px] sm:leading-normal',
  {
    variants: {
      profile: {
        true: 'sm:p-24',
        false: 'sm:px-24',
      },
    },
    defaultVariants: {
      profile: false,
    },
  },
)

interface EdmanTextBlocksProps {
  typing?: boolean
  onLastDisplayEnd?: () => void
}

function EdmanTextBlocks({
  typing = false,
  onLastDisplayEnd,
}: EdmanTextBlocksProps) {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const data = onboardingSteps[state.currentStep]
  const intl = useIntl()
  const messageKey = data.question
  const firstEnter = state.isForward && state.currentStep > state.userLastStep
  const animateSpeed = firstEnter
    ? 'fast'
    : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    (state.stepType == 'goal' && state.goalNumber != undefined) ||
      state.currentStep <= state.userLastStep
    ? undefined
    : 'fast'

  const [isLoading, setIsLoading] = useState(typing)
  const textDisplayDelay = 1000

  useTimeout(() => {
    setIsLoading(false)
  }, textDisplayDelay)

  return (
    <div className="flex flex-col gap-8">
      <EdmanTextBubble>
        {isLoading ? (
          <DotsAnimation />
        ) : (
          <ClickableWords
            selectedWords={[]}
            animateSpeed={animateSpeed}
            disabled={false}
            onLastDisplayEnd={onLastDisplayEnd}
          >
            {intl.formatMessage({ id: messageKey }, { name: state.info.name })}
          </ClickableWords>
        )}
      </EdmanTextBubble>
    </div>
  )
}

export function VideoAvatarBlock(props: {
  text: string
  videoUrl: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex max-w-800 flex-col  items-end sm:flex-row sm:justify-end',
        props.className,
      )}
    >
      <div className="z-10 mx-8 sm:mx-0 sm:-mr-44 sm:pb-40">
        <EdmanBlock hideMute text={props.text} />
      </div>
      <EdmanAvatar className="w-200 shrink-0" />
    </div>
  )
}

export const NameInputStep = () => {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state, { sync: true })
  const intl = useIntl()

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === ' ') return
    store.setInfo('name', e.target.value)
  }

  return (
    <div>
      <TextInput
        type="text"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            store.changeStep(true)
          }
        }}
        name="name"
        value={state.info.name ?? ''}
        size="extralarge"
        className="w-full rounded-full border border-purple-light2 placeholder:text-18 placeholder:font-semibold placeholder:text-purple3 focus:border-purple3"
        placeholder={intl.formatMessage({ id: 'onboarding.enterName' }) + '...'}
        autoComplete="off"
        onChange={handleInput}
      />
    </div>
  )
}
// deprecate
// export function OnboardingBlock(props: {
//   title: string
//   text: string
//   goal?: boolean
// }) {
//   return (
//     <motion.div
//       transition={{
//         duration: 0.3,
//         type: 'tween',
//         ease: 'easeInOut',
//       }}
//       initial={{ opacity: 0, x: 100 }}
//       animate={{ opacity: 1, x: 0 }}
//       exit={{ opacity: 0 }}
//       className={cn('flex items-center gap-16 rounded-4 bg-purple3 p-16')}
//     >
//       {props.goal && <div className="-m-12 text-[80px]">🎯</div>}
//       <div>
//         <div className="text-14 font-semibold text-light">{props.title}</div>
//         <div className="text-20 font-bold">{props.text}</div>
//       </div>
//     </motion.div>
//   )
// }

export const ChooseButtonContainer = classed.button(
  'flex items-center justify-center transition-all hover:opacity-80',
  {
    variants: {
      size: {
        small:
          'rounded-full px-12 py-0 text-12 font-bold sm:px-16 sm:py-4 sm:text-16',
        large: 'rounded-32 px-24 py-8 text-18 font-extrabold sm:text-18',
      },
      active: {
        false: 'bg-white text-default',
        true: 'bg-user-message text-black',
      },
      width: {
        full: '',
        half: 'xl:max-w-[50%]',
      },
    },
  },
)

function ChooseButton(props: {
  onClick: () => void
  id?: string
  size?: 'small' | 'large'
  children: React.ReactNode
  className?: string
  active: boolean
  width?: 'full' | 'half'
}) {
  return (
    <ChooseButtonContainer
      id={props.id}
      size={props.size ?? 'large'}
      active={props.active}
      className={props.className}
      onClick={props.onClick}
      width={props.width}
    >
      {props.children}
    </ChooseButtonContainer>
  )
}

export function Progress() {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const stages = onboardingSteps

  return (
    <div className="my-16 flex w-full items-center justify-center px-8 sm:mt-40">
      {!state.hideBackButton && (
        <button
          id="onboarding.mobile-back"
          onClick={() => {
            store.changeStep(false)
          }}
          className="size-32 shrink-0  opacity-30 sm:hidden"
        >
          <Icon iconName="left" className="text-black" />
        </button>
      )}
      <ProgressSteps step={state.currentStep + 1} total={stages.length - 1} />
      {/* <div className="w-20"></div> */}
    </div>
  )
}

export const ProgressSteps = (props: { step: number; total: number }) => {
  return (
    <div className="flex h-6 w-full max-w-600 gap-4">
      {range(props.total).map((i) => (
        <div
          key={i}
          className={cn(
            'flex-1 rounded-full bg-main transition-opacity',
            i >= props.step && 'opacity-20',
          )}
        ></div>
      ))}
    </div>
  )
}

export const StepContainer = classed.div(
  'flex min-h-0 w-full flex-1 flex-col gap-16 overflow-y-auto overflow-x-hidden pb-32 sm:pb-0',
  {
    variants: {
      chat: {
        false: 'items-center justify-center px-12 sm:px-0',
      },
    },
  },
)

export function OnboardingView() {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const data = onboardingSteps[state.currentStep]
  const authStore = useAppModule().authStore
  const isMobile = useIsMobile()
  const stepsWithoutNextButton: OnboardingStep[] = [
    'sex',
    'age',
    'profession',
    'level',
    // 'duration',
  ]

  //   useMount(() => {
  //     void preloadAllVideos()
  //   })

  if (
    authStore.isLogin() &&
    !authStore.state.isAnon &&
    state.completed &&
    state.hasSubscription
  ) {
    return <Navigate to={urls.main} />
  }

  if (state.goToDemo) {
    return <Navigate to={urls.demo()} />
  }

  if (state.completed) {
    authStore.trackEvent('finish_onboarding')
    const nextUrl = state.hasSubscription ? urls.main : urls.subscriptions()
    return <Navigate to={nextUrl} />
  }

  return (
    <>
      <MotionConfig
        transition={{
          duration: 0.5,
          type: 'tween',
          ease: 'easeInOut',
        }}
      >
        <div
          className={cn(
            'relative flex w-full flex-col items-center bg-light text-black transition-colors',
          )}
        >
          {/* {data.step != 'program' && data.step != 'askdemo' && <Progress />} */}
          {data.step != 'program' && <Progress />}
          <StepContainer chat={false}>
            {match(data.step)
              .with('generation', () => <CreatingProgram />)
              .with('program', () => <ProgramStep />)
              .otherwise(() => (
                <>
                  <StepView />
                  {isMobile && (
                    <NavigationButtons
                      hideNext={stepsWithoutNextButton.includes(state.stepType)}
                    />
                  )}
                </>
              ))}
          </StepContainer>
        </div>
      </MotionConfig>
    </>
  )
}

export function EdmanBlock(props: {
  text: string
  left?: boolean
  hideAvatar?: boolean
  hideMute?: boolean
  className?: string
  fast?: boolean
}) {
  const appStore = useAppModule().appStore
  const appState = useSnapshot(appStore.state)
  const ref = useRef<HTMLDivElement>(null)
  const [animate, setAnimate] = useState(false)
  const isInView = useInView(ref)
  useEffect(() => {
    if (isInView) {
      setAnimate(true)
    }
  }, [isInView])
  return (
    <motion.div
      ref={ref}
      transition={{
        delay: 0.2,
        duration: 0.3,
        type: 'tween',
        ease: 'easeInOut',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.5 }}
      className={cn(
        'relative max-w-fit whitespace-pre-wrap bg-purple-light2 px-24 pb-4 pt-8 text-20 font-bold leading-[1.2em] text-default',
        props.left ? 'rounded-r rounded-tl' : 'rounded-l rounded-tr',
        props.className,
      )}
    >
      {!props.hideMute && (
        <button
          onClick={() => {
            appStore.toggleEdmanUnmute()
          }}
          className={cn(
            'absolute top-0 rounded-8 bg-white p-4',
            props.left ? '-right-36' : '-left-36',
          )}
        >
          <Icon iconName={appState.edmanUnmute ? 'unmute' : 'mute'} />
        </button>
      )}
      <div className="text-14 font-extrabold text-main-light">
        {TUTOR_NAME}
      </div>
      <ClickableWords
        disabled={!animate}
        selectedWords={[]}
        delay={300}
        animateSpeed={props.fast ? 'fast' : 'slow'}
      >
        {props.text}
      </ClickableWords>
    </motion.div>
  )
}

function NavigationButtons(
  props: { className?: string; hideBack?: boolean; hideNext?: boolean } = {},
) {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const hideBack = state.hideBackButton
  //   const isAskDemo = state.stepType == 'askdemo'
  const isProgram = state.stepType === 'program'

  const nextButtonId = 'onboarding-next-' + state.stepType
  //   const continueText = isAskDemo ? (
  //     <FormattedMessage id="letsTrain" />
  //   ) : (
  //     <FormattedMessage
  //       id={
  //         state.stepType == 'name' && !state.info.name
  //           ? 'Continue without name'
  //           : 'Continue'
  //       }
  //     />
  //   )
  const continueText = (
    <FormattedMessage
      id={
        state.stepType == 'name' && !state.info.name
          ? 'Continue without name'
          : 'Continue'
      }
    />
  )

  const prevButton = (
    <Button
      disabled={!hideBack && !state.canGoPrev}
      rounded="full"
      bg="gray-gradient"
      size="extralarge"
      className={cn('relative hidden sm:block', hideBack && 'invisible')}
      onClick={() => {
        store.changeStep(false)
      }}
    >
      <div className="absolute inset-y-0 left-12 flex rotate-180 items-center">
        <Icon size="large" iconName="arrowRight" />
      </div>
      <div className="ml-16">
        <FormattedMessage id="Back" />
      </div>
    </Button>
  )

  const nextButton = (
    <Button
      disabled={!state.canGoNext}
      id={nextButtonId}
      rounded="full"
      bg="blue-gradient"
      size="extralarge"
      className={cn(
        props.hideNext && 'hidden',
        isProgram &&
          'fixed inset-x-12 bottom-12 left-1/2 -translate-x-1/2 xl:max-w-[800px]',
        !isProgram && 'relative w-full sm:w-auto',
      )}
      onClick={() => {
        store.changeStep(true)
      }}
    >
      <div className="mr-16">{continueText}</div>
      <div className="absolute inset-y-0 right-16 flex items-center">
        <Icon size="small" iconName="arrowRight" />
      </div>
    </Button>
  )

  //   const skipButton = (
  //     <Button
  //       rounded="full"
  //       bg="gray-gradient"
  //       size="extralarge"
  //       id="skip-demo"
  //       onClick={() => {
  //         store.skipDemo()
  //       }}
  //     >
  //       <FormattedMessage id="skipPractice" />
  //     </Button>
  //   )

  //   if (isAskDemo) {
  //     return (
  //       <div className="flex w-full flex-col justify-between gap-12 msm:fixed msm:bottom-16 msm:px-16 sm:flex-row">
  //         {skipButton}
  //         {nextButton}
  //       </div>
  //     )
  //   }

  return (
    <div
      className={cn(
        'mt-auto flex w-full justify-between gap-16 msm:fixed msm:bottom-16 msm:px-16 sm:mt-16',
        props.className,
      )}
    >
      {prevButton}
      {nextButton}
    </div>
  )
}

const AnimatedRow = ({
  index,
  item,
}: {
  index: number
  item: AppMessageKeys
}) => {
  const [scope, animate] = useAnimate()
  const [iconScope, iconAnimate] = useAnimate()

  useMount(() => {
    const controls = animate([
      [
        scope.current,
        { opacity: 1, color: '#8E9CC1' },
        { ...getGenerationAnimDelay('first', index) },
      ],

      [
        scope.current,
        {
          opacity: 1,
          color: '#000000',
        },
        { ...getGenerationAnimDelay('second') },
      ],
    ])

    const iconControls = iconAnimate([
      [
        iconScope.current,
        { opacity: 1, color: '#CFD6EB' },
        { ...getGenerationAnimDelay('first', index) },
      ],

      [
        iconScope.current,
        {
          opacity: 1,
          color: '#60D992',
        },
        { ...getGenerationAnimDelay('second') },
      ],
    ])

    return () => {
      controls.stop()
      iconControls.stop()
    }
  })

  return (
    <motion.div
      ref={scope}
      key={index}
      initial={{ opacity: 0, color: '#8E9CC1' }}
      className="flex items-center gap-8 font-nunito-7-semicondensed text-16 font-medium"
    >
      <Icon iconName="variableCheck" ref={iconScope} size="xsm" />
      <FormattedMessage id={item} />
    </motion.div>
  )
}

const ANIMATED_ITEMS: AppMessageKeys[] = [
  'onboarding.create-program-item1',
  'onboarding.create-program-item2',
  'onboarding.create-program-item3',
  'onboarding.create-program-item4',
]

const AnimatedList = () => {
  return (
    <div className="flex flex-col gap-8">
      {ANIMATED_ITEMS.map((item, index) => (
        <AnimatedRow item={item} index={index} key={item} />
      ))}
    </div>
  )
}

function CreatingProgram() {
  return (
    <div className="flex flex-col items-center">
      {/* <ProgramCreatingImage className="relative mx-auto" /> */}
      <Rive className="size-[300px]" src="/animations/generation.riv" />
      <div className="max-w-400 text-18 font-bold">
        <FormattedMessage id="Edman is generating" />
      </div>
      <div className="mb-48 mt-18 text-center text-32 font-extrabold">
        <FormattedMessage id="Please wait a little..." />
      </div>
      <AnimatedList />
    </div>
  )
}

function GoalAnswerBlock() {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const intl = useIntl()

  store.updateUserLastStep()

  const answerKey =
    state.goalNumber == undefined || state.stepType != 'goal_answer'
      ? undefined
      : (`onboarding.answer-${state.goalNumber}` as const)
  return (
    <EdmanTextBubble>
      {answerKey && (
        <ClickableWords
          selectedWords={[]}
          delay={500}
          animateSpeed="fast"
          disabled={false}
        >
          {intl.formatMessage({ id: answerKey })}
        </ClickableWords>
      )}
    </EdmanTextBubble>
  )
}

export const HelloStep = () => {
  const store = useOnboardingStore()

  const handleLastDisplayEnd = () => {
    store.updateUserLastStep()
  }

  return <EdmanTextBlocks onLastDisplayEnd={handleLastDisplayEnd} />
}

function ProgramStepBlock(props: {
  title: React.ReactNode
  items: React.ReactNode[]
}) {
  return (
    <EdmanTextBubble profile={true}>
      <div className="mb-12 text-18 font-bold">{props.title}</div>
      {props.items.map((x, i) => (
        <div
          key={i}
          className="flex items-center gap-8 border-t-2 border-gray-light py-8"
        >
          {x}
        </div>
      ))}
    </EdmanTextBubble>
  )
}

const PROGRAM_ITEMS: AppMessageKeys[] = [
  'onboarding.learningPlan-items1',
  'onboarding.learningPlan-items2',
  'onboarding.learningPlan-items3',
  'onboarding.learningPlan-items4',
  'onboarding.learningPlan-items5',
]
function ProgramStep() {
  const profileData = useOnboardingStore().getProfileData()

  return (
    <div className="msm:pb-24">
      <div className="relative mx-auto my-24 w-fit text-center text-28 font-extrabold leading-tight sm:text-36">
        <FormattedMessage id="yourPersonal" /> <br className="sm:hidden" />
        <span className="text-main">
          <FormattedMessage id="educationPlan" />
        </span>{' '}
        <FormattedMessage id="ready" />
      </div>
      <StepDataContainer className="mb-16">
        <div className="flex flex-col gap-16 sm:flex-row sm:gap-24">
          <ProgramStepBlock
            title={<FormattedMessage id="onboarding.yourProfile:" />}
            items={profileData.map((x) => (
              <>
                <div className="text-20">{x.icon}</div>
                <div className="text-14 font-semibold">
                  {x.key === 'duration' ? (
                    <FormattedMessage
                      id="onboarding.minutesPerDay"
                      values={{ minutes: x.value }}
                    />
                  ) : (
                    x.text
                  )}
                </div>
              </>
            ))}
          />
          <ProgramStepBlock
            title={<FormattedMessage id="onboarding.youWillReceive:" />}
            items={PROGRAM_ITEMS.map((x) => (
              <>
                <div className="text-20">
                  <Icon iconName="done" />
                </div>
                <div className="text-14 font-semibold">
                  <FormattedMessage id={x} />
                </div>
              </>
            ))}
          />
        </div>
      </StepDataContainer>
      <NavigationButtons />
    </div>
  )
}

// function AskDemoStep() {
//   return (
//     <div>
//       <ImageHideBeforeLoad
//         className="mx-auto mt-36 flex h-full flex-col justify-between"
//         src="/images/onboarding/rocket.png"
//       />
//     </div>
//   )
// }

export function EdmanOnboardingAvatar(props: {
  className?: string
  isSubscription?: boolean
}) {
  const size = props.isSubscription ? 206 : 260
  return (
    <img
      src="/images/Pavel_Logo.PNG"
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className={cn('rounded-full object-cover', props.className)}
      alt={TUTOR_NAME}
    />
  )
}

export const stepComponents: Record<OnboardingStep, React.ReactNode> = {
  hello: <HelloStep />,
  name: <NameInputStep />,
  age: <AgeSelectionStep />,
  interests: <InterestSelectionStep />,
  profession: <ProfessionSelectionStep />,
  level: <LanguageLevelSelectionStep />,
  goal: <GoalSelectionStep />,
  goal_answer: <GoalAnswerBlock />,
  improves: <ImproveSelectionStep />,
  duration: <DurationSelectionStep />,
  sex: <SexSelectionStep />,
  generation: <></>,
  program: <></>,
  //   askdemo: <AskDemoStep />,
}

export function StepView() {
  const store = useOnboardingStore()
  const state = useSnapshot(store.state)
  const [isLoading, setIsLoading] = useState(true)
  const stepsWithoutNextButton: OnboardingStep[] = [
    'sex',
    'age',
    'profession',
    'level',
    // 'duration',
  ]
  const stepsWithAvatar: OnboardingStep[] = ['hello', 'goal_answer']

  const steps = onboardingSteps
  const step = steps[state.currentStep]
  const isMobile = useIsMobile()

  const handleLastDisplayEnd = () => {
    setIsLoading(false)
    store.updateUserLastStep()
  }

  useLayoutEffect(() => {
    setIsLoading(true)
  }, [step])

  const firstView = state.currentStep > state.userLastStep
  const firstEnter = state.isForward && firstView
  const needAnimate = firstEnter || state.currentStep <= state.userLastStep

  const showAvatar = stepsWithAvatar.includes(step.step)

  return (
    <motion.div
      className="my-auto flex w-full max-w-[860px] flex-col"
      key={state.stepType + state.goalNumber}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className="mb-32 flex justify-center text-32 font-bold mxs:mb-8">
        {showAvatar ? (
          <EdmanOnboardingAvatar className="flex items-center justify-center" />
        ) : step.title ? (
          <FormattedMessage id={step.title as AppMessageKeys} />
        ) : null}
      </div>
      <StepDataContainer key={state.currentStep}>
        {!showAvatar && (
          <EdmanTextBlocks
            onLastDisplayEnd={handleLastDisplayEnd}
            typing={firstView}
          />
        )}
        {needAnimate ? (
          <motion.div
            className="mt-32 msm:mt-8"
            initial={{
              opacity: state.currentStep > state.userLastStep ? 0 : 1,
            }}
            animate={{
              opacity:
                isLoading && state.currentStep > state.userLastStep ? 0 : 1,
            }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            {stepComponents[state.stepType]}
          </motion.div>
        ) : (
          <div className="mt-24">{stepComponents[state.stepType]}</div>
        )}
      </StepDataContainer>
      {!isMobile && (
        <NavigationButtons
          hideNext={stepsWithoutNextButton.includes(state.stepType)}
        />
      )}
    </motion.div>
  )
}

const StepDataContainer = classed.div('mb-120')
